import React from 'react';
import {
  Box,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

const BannerCard = ({ 
  banner, 
  onClick, 
  height = { xs: 200, md: 300 },
  showButton = true,
  buttonText = 'shopNow',
  ...props 
}) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleBannerClick = () => {
    if (onClick) onClick(banner);
  };

  const handleButtonClick = (e) => {
    e.stopPropagation();
    handleBannerClick();
  };

  // Get appropriate image based on device
  const imageUrl = isMobile 
    ? banner?.mobile_image?.medium_url || banner?.mobile_image?.url || '/api/placeholder/800/300'
    : banner?.image?.large_url || banner?.image?.url || '/api/placeholder/1200/300';

  return (
    <Box
      onClick={handleBannerClick}
      sx={{
        position: 'relative',
        height: height,
        borderRadius: 2,
        overflow: 'hidden',
        cursor: banner.link_url ? 'pointer' : 'default',
        backgroundImage: `url(${imageUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'linear-gradient(135deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 100%)',
          transition: 'background 0.4s ease',
        },
        '&:hover': {
          transform: 'scale(1.02)',
          boxShadow: `0 20px 40px ${theme.palette.primary.main}30`,
          '&::before': {
            background: 'linear-gradient(135deg, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.4) 100%)',
          },
          '& .banner-content': {
            transform: 'translateY(-5px)',
          },
          '& .banner-button': {
            transform: 'scale(1.05)',
            boxShadow: '0 8px 25px rgba(255,215,0,0.4)',
          },
        },
        ...props.sx
      }}
    >
      {/* Content Overlay */}
      <Box
        className="banner-content"
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          textAlign: 'center',
          padding: 3,
          transition: 'transform 0.4s ease',
        }}
      >
        <Box sx={{ maxWidth: 600 }}>
          <Typography
            variant={isMobile ? 'h4' : 'h3'}
            fontWeight="bold"
            gutterBottom
            sx={{
              textShadow: '2px 2px 8px rgba(0,0,0,0.8)',
              lineHeight: 1.2,
              mb: 2,
              animation: 'fadeInUp 0.8s ease-out',
            }}
          >
            {banner.title?.[i18n.language] || banner.title?.en || banner.title}
          </Typography>
          
          {banner.subtitle && (
            <Typography
              variant={isMobile ? 'body1' : 'h6'}
              sx={{
                mb: 3,
                textShadow: '1px 1px 4px rgba(0,0,0,0.8)',
                lineHeight: 1.4,
                animation: 'fadeInUp 0.8s ease-out 0.2s both',
              }}
            >
              {banner.subtitle?.[i18n.language] || banner.subtitle?.en || banner.subtitle}
            </Typography>
          )}

          {showButton && banner.link_url && (
            <Button
              className="banner-button"
              variant="contained"
              size={isMobile ? 'medium' : 'large'}
              onClick={handleButtonClick}
              sx={{
                backgroundColor: 'white',
                color: theme.palette.primary.main,
                px: 4,
                py: 1.5,
                fontSize: isMobile ? '0.9rem' : '1rem',
                fontWeight: 600,
                borderRadius: 50,
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                animation: 'fadeInUp 0.8s ease-out 0.4s both',
                '&:hover': {
                  backgroundColor: theme.palette.primary.main,
                  color: 'white',
                  transform: 'scale(1.05)',
                  boxShadow: '0 8px 25px rgba(255,215,0,0.4)',
                },
              }}
            >
              {t(buttonText, 'Shop Now')}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default BannerCard;
